﻿import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { requirementsService, type RequirementStatus } from '@/services/requirements.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

function toStatusCode(name: string): string {
  return name.trim().toUpperCase().replace(/\s+/g, '_').replace(/[^A-Z0-9_]/g, '');
}

interface FormValues {
  name: string;
  code: string;
  color: string;
  display_order: number;
  is_default: boolean;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  status?: RequirementStatus;
  onClose: () => void;
  onSuccess: () => void;
}

export function StatusFormDialog({ open, mode, status, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', code: '', color: '#94a3b8', display_order: 0, is_default: false },
  });

  const nameValue = watch('name');
  const colorValue = watch('color');
  const isDefault = watch('is_default');

  useEffect(() => {
    if (!isEdit && nameValue) {
      setValue('code', toStatusCode(nameValue));
    }
  }, [nameValue, isEdit, setValue]);

  useEffect(() => {
    if (open) {
      reset({
        name: status?.name ?? '',
        code: status?.code ?? '',
        color: status?.color ?? '#94a3b8',
        display_order: status?.display_order ?? 0,
        is_default: status?.is_default ?? false,
      });
    }
  }, [open, status, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const input = {
        name: values.name.trim(),
        code: values.code.trim().toUpperCase(),
        color: values.color,
        display_order: Number(values.display_order),
        is_default: values.is_default,
      };
      if (isEdit && status) {
        await requirementsService.updateStatus(status.id, input);
      } else {
        await requirementsService.createStatus(input);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Status updated' : 'Status created');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'statuses'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Status' : 'New Status'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="req-status-name">Status Name *</Label>
            <Input
              id="req-status-name"
              {...register('name', { required: 'Name is required' })}
              placeholder="e.g. Under Review"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-status-code">Status Code *</Label>
            <Input
              id="req-status-code"
              {...register('code', { required: 'Code is required', maxLength: { value: 50, message: 'Max 50 characters' } })}
              onChange={(e) => setValue('code', e.target.value.toUpperCase())}
              placeholder="e.g. UNDER_REVIEW"
              maxLength={50}
            />
            {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label>Color</Label>
            <div className="flex items-center gap-3">
              <div
                className="h-8 w-8 rounded-full border border-border shrink-0"
                style={{ backgroundColor: colorValue }}
              />
              <input
                type="color"
                value={colorValue}
                onChange={(e) => setValue('color', e.target.value)}
                className="h-9 w-14 cursor-pointer rounded border border-border bg-transparent p-0.5"
              />
              <Input
                {...register('color', { pattern: { value: /^#[0-9A-Fa-f]{6}$/, message: 'Must be a valid hex color' } })}
                placeholder="#94a3b8"
                maxLength={7}
                className="font-mono text-sm"
                onChange={(e) => {
                  const v = e.target.value;
                  setValue('color', v);
                }}
              />
            </div>
            {errors.color && <p className="text-xs text-destructive">{errors.color.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-status-order">Display Order</Label>
            <Input
              id="req-status-order"
              type="number"
              min={0}
              {...register('display_order', { valueAsNumber: true })}
              placeholder="0"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="req-status-default"
              checked={isDefault}
              onChange={(e) => setValue('is_default', e.target.checked)}
            />
            <Label htmlFor="req-status-default" className="cursor-pointer font-normal">
              Set as default status for new requirements
            </Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
